import {
	UIMarket,
	UISerializableSwapRecord,
	TRADE_PRECISION,
} from '@drift/common';
import { SpotMarketConfig } from '@drift-labs/sdk';
import { useSpotMarkets } from 'src/hooks/spotMarkets/useSpotMarkets';
import Env from 'src/environmentVariables/EnvironmentVariables';
import handleSpecialRendering from 'src/utils/handleSpecialRendering';
import { getUrlForTx } from 'src/utils/explorer';

interface Params {
	swap: UISerializableSwapRecord;
	isSellDirection: boolean;
}

export default function useSwapHistoryTableData({
	swap,
	isSellDirection,
}: Params) {
	const { amountIn, amountOut, inMarketIndex, outMarketIndex, ts, txSig } =
		swap;

	const inMarket = UIMarket.createSpotMarket(inMarketIndex);
	const outMarket = UIMarket.createSpotMarket(outMarketIndex);
	const spotMarkets = useSpotMarkets();

	const fromMarketSymbol = Object.values(spotMarkets).find((spotMarket) =>
		(inMarket.market as SpotMarketConfig).mint.equals(spotMarket.mint)
	)?.symbol;
	const toMarketSymbol = Object.values(spotMarkets).find((spotMarket) =>
		(outMarket.market as SpotMarketConfig).mint.equals(spotMarket.mint)
	)?.symbol;

	const buySwapPrice = (() => {
		try {
			const swapPrice = amountOut.toNum() / amountIn.toNum();
			return handleSpecialRendering(swapPrice.toString(), {
				fallback: swapPrice.toLocaleString(Env.locale, {
					maximumFractionDigits: TRADE_PRECISION,
					minimumFractionDigits: TRADE_PRECISION,
				}),
			});
		} catch {
			return amountOut.div(amountIn).toTradePrecision();
		}
	})();

	const sellSwapPrice = (() => {
		try {
			const swapPrice = amountIn.toNum() / amountOut.toNum();
			return handleSpecialRendering(swapPrice.toString(), {
				fallback: swapPrice.toLocaleString(Env.locale, {
					maximumFractionDigits: TRADE_PRECISION,
					minimumFractionDigits: TRADE_PRECISION,
				}),
			});
		} catch {
			return amountIn.div(amountOut).toTradePrecision();
		}
	})();

	const swapPrice = isSellDirection ? sellSwapPrice : buySwapPrice;

	const swapPriceText = `${swapPrice} ${
		isSellDirection
			? inMarket.uiSymbols.baseAssetSymbol
			: outMarket.uiSymbols.baseAssetSymbol
	} per ${
		isSellDirection
			? outMarket.uiSymbols.baseAssetSymbol
			: inMarket.uiSymbols.baseAssetSymbol
	}`;

	const dateFromTimestamp = new Date(ts.toNumber() * 1000);

	const scannerUrl = getUrlForTx(txSig);

	return {
		amountIn,
		amountOut,
		fromMarketSymbol,
		toMarketSymbol,
		inMarket,
		outMarket,
		swapPriceText,
		dateFromTimestamp,
		scannerUrl,
	};
}
